import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { themeMediaQuery } from '@utils/styledTheme.util';
import styled from 'styled-components';
import { BlogCarousel } from '@components/BlogCarousel';
import { WeddingWebsite } from '@components/WeddingWebsite';
import { Layout, Seo } from '@layout';
import {
  AllTogetherSection,
  AllTogetherSectionMobile,
  AnswersCarousel,
  FlexibleRSVPQuestionsSection,
  HeroSection,
  KnowWhoToExpectSection,
  PlusOnes,
  RSVPByNameSection,
  RSVPTrackingSection,
  RunTheShow,
  TestSendTrack,
  SmartRSVPInviteFeatureSection,
  FindPerfectInvitationSection,
  QuestionBuilderSection,
  PlusOneControls,
} from '@sections/online-rsvp';
import { AbcSection } from '@sections/shared/AbcSection';
import { SpeechBubbles } from '@sections/shared/SpeechBubbles';
import { PersonalSaveTheDatesSection } from '@sections/shared/PersonalSaveTheDatesSection';
import { EasyForYouSection } from '@sections/shared/EasyForYouSection';
import { ColoredBlockSection } from '@sections/shared/ColoredBlockSection';
import { FaqList } from '@components/FaqList';
import { Box } from '@components/Box';
import TestimonialSection from '@sections/shared/TestimonialSection/TestimonialSection';
import { ConnectedGuestListStatic } from '@sections/online-rsvp/ConnectedGuestListSection/ConnectedGuestListStatic';
import { PhotoCredit } from '@components/PhotoCredit';
import { useTranslation } from 'react-i18next';

// TODO: can we get it automatically during build somehow?
const page: MarcomPageName = 'online-rsvp';

export const AllTogether = styled(Box)`
  width: 100%;
  .all-together-section-mobile {
    ${themeMediaQuery('sm')} {
      display: none;
    }
  }
  .all-together-section-desktop {
    ${themeMediaQuery('below_sm')} {
      display: none;
    }
  }
`;

const OnlineRsvp = () => {
  const data = useStaticQuery(graphql`
    query {
      rsvpBlogs {
        id
        blogs {
          link
          title
          excerpt
          mediaLink
        }
      }
    }
  `);

  const { t } = useTranslation();
  const speechBubbles = t('onlineRsvp:speechBubbles');

  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="purple6"
      transparentHeaderButtonVariant="primary"
      transparentHeaderLinkVariant="primary"
      page={page}
    >
      <Seo
        title={t('onlineRsvp:seo.title')}
        canonicalURL={'https://withjoy.com/online-rsvp/'}
        description={t('onlineRsvp:seo.content')}
        ogImage={'common/meta_share/invites-rsvp.jpg'}
      />
      <HeroSection />
      <SmartRSVPInviteFeatureSection />
      <KnowWhoToExpectSection />
      <AllTogether>
        <AllTogetherSection />
        <AllTogetherSectionMobile />
      </AllTogether>
      <PlusOnes />
      <PlusOneControls />
      <FlexibleRSVPQuestionsSection />
      <QuestionBuilderSection />
      <AnswersCarousel />
      <EasyForYouSection pageVariant="weddingInvitations" lottieBackground="sitewide/block_emotional-break/easyforyou1/images/img_0.jpg" />
      <RSVPTrackingSection page="weddingInvitations" />
      <SpeechBubbles
        eyebrow={speechBubbles.eyebrow}
        title={speechBubbles.title}
        titleMaxWidth="300px"
        body={speechBubbles.content}
        variant="blue"
        imagesSrc={speechBubbles.image}
        isHorizontal={true}
        backgroundColor="yellow1"
      />
      <TestSendTrack />
      <RSVPByNameSection />
      <RunTheShow backgroundColor="blue6" />
      <WeddingWebsite
        frameVariant="inks"
        colorVariant="green"
        text={t('weddingWebsite:invitations')}
        pageVariant="rsvp"
        btnVariant="invertedGreenPrimary"
        image={t('onlineRsvp:weddingWebsite.image')}
      />
      <FindPerfectInvitationSection />
      <PersonalSaveTheDatesSection
        backgroundColorPalette="yellow"
        colorPalette="blue"
        isImageLeft={true}
        textData={t('onlineRsvp:personalSaveTheDatesSectionData')}
        sectionId="RSVP_Save_the_Dates_Cross"
      />
      <ConnectedGuestListStatic />
      <ColoredBlockSection backgroundColor="blue1" sideHeaderText={t('onlineRsvp:faq.title')} sectionId="RSVP_FAQ">
        <FaqList accordion={true} questionNodes={[...t('faq:questions.invitation'), ...t('faq:questions.general')]} color="blue6" />
      </ColoredBlockSection>
      <BlogCarousel blogs={data.rsvpBlogs.blogs} sectionId="RSVP_Blog" />
      <TestimonialSection carouselVariant={'left'} backgroundColor="blue6" quotes={t('testimonials:invites')} mobileQuoteIndex={2} />
      <AbcSection page={page} abcSectionId="RSVP_ABC_CTA" />
      <Box style={{ position: 'relative' }}>
        <PhotoCredit color="green6" id="photoCreditOnlineRSVPHero" />
      </Box>
    </Layout>
  );
};

export default OnlineRsvp;
